// frontend/src/modules/hr/pages/MySchedulePage.tsx
//
// Employee-facing "My schedule" page — two week strips (this week + next)
// built from GET /api/hr/schedule/me. Live-refreshes every 30s (usePoll,
// pauses while the tab is hidden — same pattern as NotificationCenter).
// Handles: no employee profile (404 not_employee), scheduling disabled for
// the tenant, and the normal 14-cell grid with override/leave/overnight
// treatment.

import * as React from 'react';
import { CalendarClock } from 'lucide-react';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { LoadingState } from '../../../design-system/components/LoadingState';
import { VISUAL, statusPill } from '../../../design-system/visual';
import { ApiError } from '../../../core/api-client';
import { usePoll } from '../../monitoring/usePoll';
import { hrApi } from '../api';
import type { MySchedule, ExpectedShift } from '../types';

function fmtTime(t: string): string {
  return t.length >= 5 ? t.slice(0, 5) : t;
}

function fmtRange(shift: ExpectedShift): string {
  if (!shift.expected || !shift.start || !shift.end) return '—';
  const overnight = shift.end < shift.start;
  return `${fmtTime(shift.start)} → ${fmtTime(shift.end)}${overnight ? ' (+1)' : ''}`;
}

function fmtDayLabel(date: string): { weekday: string; day: string } {
  const d = new Date(`${date}T00:00:00Z`);
  const weekday = d.toLocaleDateString(undefined, { weekday: 'short', timeZone: 'UTC' });
  const day = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
  return { weekday, day };
}

export function MySchedulePage() {
  const [data, setData] = React.useState<MySchedule | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [status, setStatus] = React.useState<number | null>(null);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  // True when a poll after a successful first load failed — we keep rendering
  // the last-good schedule instead of blowing it away with an error screen.
  const [stale, setStale] = React.useState(false);
  const dataRef = React.useRef<MySchedule | null>(null);

  const load = React.useCallback(async () => {
    try {
      const res = await hrApi.getMySchedule();
      dataRef.current = res;
      setData(res);
      setStatus(null);
      setErrorMessage(null);
      setStale(false);
    } catch (err: any) {
      const errStatus = err instanceof ApiError ? err.status : null;
      // not_employee (404) is terminal — that status won't flip mid-session,
      // so always treat it as a first-load-style failure. Any other error
      // after data has already loaded once is treated as a transient poll
      // failure: keep the last-good schedule on screen with a stale note.
      if (errStatus !== 404 && dataRef.current) {
        setStale(true);
        setErrorMessage(err?.message ?? "Couldn't refresh your schedule.");
      } else {
        dataRef.current = null;
        setData(null);
        setStatus(errStatus);
        setErrorMessage(err?.message ?? 'Could not load your schedule.');
        setStale(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // 404 = user has no employee profile (`not_employee`) — that won't change
  // mid-session, so stop polling instead of hammering a known-404 endpoint
  // (mirrors the HomePage LocalTimer pattern).
  const notEmployee = status === 404;
  usePoll(load, 30_000, !notEmployee);

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div>
      <PageHeader
        eyebrow="Team / My schedule"
        title="My schedule"
        description="Your expected shifts for this week and next."
      />
      <div className="px-6 sm:px-8 lg:px-10 pb-12 space-y-6">
        {loading && !data && <LoadingState variant="page" label="Loading your schedule" />}

        {!loading && status === 404 && (
          <div className={[VISUAL.bento, 'p-10 text-center'].join(' ')}>
            <CalendarClock className="h-8 w-8 mx-auto text-muted-foreground/50 mb-3" />
            <p className="text-sm font-medium text-foreground">No employee profile found</p>
            <p className="text-[13px] text-muted-foreground mt-1.5 max-w-sm mx-auto">
              You don&apos;t have an employee record yet. Ask your admin to add you to HR to see your schedule.
            </p>
          </div>
        )}

        {!loading && status !== 404 && !data && errorMessage && (
          <div className={[VISUAL.bento, 'p-10 text-center'].join(' ')}>
            <p className="text-sm text-muted-foreground">{errorMessage}</p>
          </div>
        )}

        {data && (
          <>
            {!data.scheduling_enabled && (
              <div className={[VISUAL.bento, 'p-5 flex items-center gap-3'].join(' ')}>
                <CalendarClock className="h-5 w-5 text-muted-foreground/70 shrink-0" />
                <p className="text-[13px] text-muted-foreground">
                  Your team isn&apos;t using shift scheduling — your standard hours apply.
                </p>
              </div>
            )}

            {stale && (
              <p
                className="text-[12px] text-muted-foreground/70"
                data-testid="schedule-stale-indicator"
              >
                Couldn&apos;t refresh — showing last loaded schedule.
              </p>
            )}

            <WeekStrip label="This week" days={data.days.slice(0, 7)} cells={data.cells.slice(0, 7)} today={today} />
            <WeekStrip label="Next week" days={data.days.slice(7, 14)} cells={data.cells.slice(7, 14)} today={today} />
          </>
        )}
      </div>
    </div>
  );
}

function WeekStrip({
  label, days, cells, today,
}: { label: string; days: string[]; cells: ExpectedShift[]; today: string }) {
  return (
    <div className={[VISUAL.bento, 'p-6 sm:p-8'].join(' ')}>
      <p className="text-[11px] uppercase tracking-[0.14em] font-semibold text-muted-foreground/80 mb-4">{label}</p>
      <div className="grid grid-cols-1 sm:grid-cols-7 gap-3">
        {days.map((date, i) => (
          <DayCell key={date} date={date} shift={cells[i]} isToday={date === today} />
        ))}
      </div>
    </div>
  );
}

function DayCell({ date, shift, isToday }: { date: string; shift: ExpectedShift; isToday: boolean }) {
  const { weekday, day } = fmtDayLabel(date);
  const isChanged = shift?.source === 'override';
  const isLeave = shift?.source === 'leave';
  const isOff = !shift || !shift.expected;

  return (
    <div
      className={[
        'rounded-2xl p-3.5 flex flex-col gap-2 ring-1',
        isToday
          ? 'bg-saffron/10 ring-saffron/40'
          : 'bg-card/60 dark:bg-white/[0.03] ring-border/50 dark:ring-white/10',
      ].join(' ')}
      data-testid={`schedule-cell-${date}`}
    >
      <div className="flex items-center justify-between gap-1.5">
        <div>
          <p className="text-[10.5px] uppercase tracking-[0.10em] font-semibold text-muted-foreground/80">{weekday}</p>
          <p className="text-[12.5px] font-semibold text-foreground">{day}</p>
        </div>
        {isToday && <span className="text-[9.5px] uppercase tracking-[0.10em] font-bold text-saffron">Today</span>}
      </div>

      <div className="flex flex-wrap gap-1.5">
        {isChanged && <span className={statusPill('info')}>Changed</span>}
        {isLeave && <span className={statusPill('warning')}>On leave</span>}
        {isOff && !isLeave && <span className={statusPill('muted')}>Off</span>}
      </div>

      <p className="text-[12.5px] font-mono text-foreground/90">{fmtRange(shift)}</p>
      {shift?.template_name && (
        <p className="text-[11px] text-muted-foreground truncate">{shift.template_name}</p>
      )}
    </div>
  );
}
